import { ICoordPair } from "@/modules/ToolModule/TwoPointsPicker";
import { LayerComponentBase } from "@/stellar-globe-components";
import { range } from "@/utils/range";
import { deg2rad, EquatorialCoord } from "@hscmap/angle";
import { Layer, path, PointEvent, View, PointEventLayer } from "@hscmap/stellar-globe";
import { vec3 } from "gl-matrix";
import { CreateElement } from 'vue';
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { V3, V4 } from '@/utils/math';

class LineSegmentLayer extends Layer {
  private pr!: path.Renderer

  constructor(readonly vm: LineSegment) {
    super()
  }

  private unwatch!: Array<() => void>

  onAdd() {
    this.pr = new path.Renderer(this.globe.gl)
    this.pr.darkenNarrowLine = false
    this.pr.blendMode = path.BlendMode.NORMAL
    this.unwatch = [
      this.vm.$watch('value', this.refresh),
      this.vm.$watch('color', this.refresh),
    ]
    this.refresh()
  }

  onRemove() {
    this.unwatch.forEach((f) => f())
    this.pr.release()
  }

  private refresh = () => {
    const coordPair = this.vm.value
    if (coordPair) {
      const p = this.path()
      this.pr.setPaths([p])
    }
    this.globe.requestRefresh()
  }

  private path() {
    const { c0, c1 } = this.vm.value!
    const p0 = c0.xyz
    const p1 = c1.xyz
    const n = 1 + Math.floor(vec3.dist(p0, p1) / deg2rad(0.5))
    const points = range(n + 1).map((i) => {
      const t = i / n
      const x = t * p0[0] + (1 - t) * p1[0]
      const y = t * p0[1] + (1 - t) * p1[1]
      const z = t * p0[2] + (1 - t) * p1[2]
      const r = Math.sqrt(x * x + y * y + z * z)
      return new path.Point(
        [x / r, y / r, z / r],
        this.vm.color,
        0,
      )
    })
    return new path.Path(points, false)
  }

  render(view: View) {
    if (this.vm.value) {
      this.pr.render(view)
    }
  }
}

@Component
export class LineSegment extends LayerComponentBase<LineSegmentLayer> {
  @Prop({ required: true })
  value?: ICoordPair

  @Prop({ required: true })
  color!: V4

  newLayer() {
    return new LineSegmentLayer(this)
  }
}

class EdgeHandleLayer extends PointEventLayer {
  constructor(readonly vm: EdgeHandle) {
    super()
  }

  hit(e: PointEvent) {
    const [x0, y0] = e.coord2offset(this.vm.value)
    const [x, y] = e.coord2offset(e.coord)
    const r = 8
    return Math.abs(x0 - x) <= r && Math.abs(y0 - y) < r
  }

  drag(e: PointEvent) {
    this.vm.$emit('input', e.coord)
  }

  enter() {
    this.vm.Store.cursor = 'move'
  }

  leave() {
    this.vm.Store.cursor = null
  }
}

@Component
class EdgeHandle extends LayerComponentBase<EdgeHandleLayer> {
  @Prop({ required: true })
  value!: EquatorialCoord

  @Prop({ default: 8 })
  radius!: number

  newLayer() {
    return new EdgeHandleLayer(this)
  }
}

class LineHandleLayer extends PointEventLayer {
  constructor(readonly vm: LineHandle) {
    super()
  }

  hit(e: PointEvent) {
    const [x0, y0] = e.coord2offset(this.vm.value.c0)
    const [x1, y1] = e.coord2offset(this.vm.value.c1)
    const [x2, y2] = e.coord2offset(e.coord)
    const x = x2 - x0
    const y = y2 - y0
    const X = x1 - x0
    const Y = y1 - y0
    const R = X * X + Y * Y
    const r = Math.sqrt(R)
    const a = (X * x + Y * y) / R
    const b = (-Y * x + X * y) / r
    return 0 <= a && a <= 1 && -8 <= b && b <= 8
  }

  o!: V3
  c0!: V3
  c1!: V3
  dragStart(e: PointEvent) {
    this.o = e.coord.xyz
    this.c0 = this.vm.value.c0.xyz
    this.c1 = this.vm.value.c1.xyz
  }

  drag(e: PointEvent) {
    const d = vec3.sub(vec3.create(), e.coord.xyz, this.o)
    const c0 = EquatorialCoord.fromXyz(vec3.add(vec3.create(), this.c0, d) as any)
    const c1 = EquatorialCoord.fromXyz(vec3.add(vec3.create(), this.c1, d) as any)
    this.vm.$emit('input', { c0, c1 })
  }

  enter(e: PointEvent) {
    this.vm.Store.cursor = 'move'
  }

  leave() {
    this.vm.Store.cursor = null
  }
}

@Component
class LineHandle extends LayerComponentBase<LineHandleLayer> {
  @Prop({ required: true })
  value!: ICoordPair

  newLayer() {
    return new LineHandleLayer(this)
  }
}

@Component
export class EditableLine extends Vue {
  @Prop({ required: true })
  value!: ICoordPair

  @Prop({ default: () => [1, 0, 1, 0.75] })
  color!: V4

  render(h: CreateElement) {
    if (this.value != null) {
      return h('div', [
        h(LineSegment, { props: { value: this.value, color: this.color } }),
        h(LineHandle, { props: { value: this.value }, on: { input: (e: ICoordPair) => this.$emit('input', e) } }),
        h(EdgeHandle, {
          props: { value: this.value.c0 },
          on: { input: (e: EquatorialCoord) => this.$emit('input', { ...this.value, c0: e }) },
        }),
        h(EdgeHandle, {
          props: { value: this.value.c1 },
          on: { input: (e: EquatorialCoord) => this.$emit('input', { ...this.value, c1: e }) },
        }),
      ])
    }
  }
}
